import React, { ComponentType, FunctionComponent, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { makeStyles } from '@material-ui/core/styles';
import { CircularProgress } from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { httpClient } from '../../providers/dataProvider';
import useConfig from '../../hooks/useConfig';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 Mo
const ACCEPTED_MIME_TYPE = 'application/pdf';

const useStyles = makeStyles({
  container: {
    marginTop: '8px',
  },
  dropzone: {
    border: '2px dashed #ccc',
    borderRadius: '4px',
    padding: '16px',
    textAlign: 'center',
    cursor: 'pointer',
    color: '#666',
    transition: 'border-color 0.2s, background-color 0.2s',
  },
  dropzoneActive: {
    borderColor: '#1976d2',
    backgroundColor: 'rgba(25, 118, 210, 0.04)',
  },
  dropzoneDisabled: {
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  icon: {
    marginBottom: '4px',
  },
});

/**
 * Renders a download button for the validation order's current PDF
 * attachment (if any) plus a dropzone to upload a replacement. Replacing
 * the file re-triggers parsing automatically when the record's status is
 * "error" (handled server-side by ValidationOrdersController#update).
 *
 * @param Component
 */
function withValidationOrderPdf(
  Component: ComponentType<any>
): FunctionComponent {
  const Extension: FunctionComponent<Record<string, any>> = (props) => {
    const { record, source } = props;
    const classes = useStyles();
    const notify = useNotify();
    const refresh = useRefresh();
    const t = useTranslate();
    const config = useConfig();
    const [uploading, setUploading] = useState(false);

    const uploadFile = async (file: File) => {
      setUploading(true);

      try {
        const { json } = await httpClient(`${config.apiUrl}/attachments`, {
          method: 'POST',
          body: JSON.stringify({
            name: file.name,
            mime_type: file.type,
            size: file.size,
            kind: 'validation_order',
            file_uuid: uuidv4(),
          }),
        });
        const { id, upload_url } = json as {
          id: string | number;
          upload_url: string;
        };

        await axios.put(upload_url, file, {
          headers: { 'Content-Type': file.type },
        });

        await httpClient(`${config.apiUrl}/validation_orders/${record.id}`, {
          method: 'PUT',
          body: JSON.stringify({ validation_order: {}, attachment_id: id }),
        });

        notify('resource.validation_orders.pdf.notify.replaced', 'success');
        refresh();
      } catch {
        notify('resource.validation_orders.pdf.notify.replace_failed', 'error');
      } finally {
        setUploading(false);
      }
    };

    const onDrop = (acceptedFiles: File[], rejectedFiles: any[]) => {
      if (acceptedFiles.length > 0) {
        uploadFile(acceptedFiles[0]);

        return;
      }

      if (rejectedFiles.length > 0) {
        notify('resource.validation_orders.pdf.notify.replace_failed', 'error');
      }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: ACCEPTED_MIME_TYPE,
      multiple: false,
      maxSize: MAX_FILE_SIZE,
      disabled: uploading,
      onDrop,
    });

    if (!record || !source) return null;

    const attachment = record[source];

    const buttons = attachment
      ? [
          {
            id: 'download',
            key: 'download',
            href: attachment.src,
            label: attachment.title,
            icon: true,
          },
        ]
      : [];

    return (
      <>
        {attachment && (
          <Component {...props} record={{ ...record, [source]: buttons }} />
        )}
        <div className={classes.container}>
          <div
            {...getRootProps()}
            className={`${classes.dropzone} ${
              isDragActive ? classes.dropzoneActive : ''
            } ${uploading ? classes.dropzoneDisabled : ''}`}
          >
            <input {...getInputProps()} />
            {uploading ? (
              <CircularProgress size={24} className={classes.icon} />
            ) : (
              <CloudUploadIcon className={classes.icon} fontSize="large" />
            )}
            <div>
              {uploading
                ? t('resource.validation_orders.pdf.uploading.label')
                : t('resource.validation_orders.pdf.dropzone.label')}
            </div>
          </div>
        </div>
      </>
    );
  };

  Extension.displayName = 'withExtension(ValidationOrderPdf)';

  return Extension;
}

export default withValidationOrderPdf;
